import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EchoContext from 'context/EchoContext';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import { roles } from 'common/constants/options';

/**
 * Hook to listen for new plan requests via WebSocket (admin only).
 * Subscribes to the private admins channel.
 * Shows an info snackbar when a new plan request is submitted.
 */
function usePlanRequestAdminListener() {
  const { t } = useTranslation(['notifications']);
  const dispatch = useDispatch();
  const echoContext = useContext(EchoContext);
  const echo = echoContext?.echo || null;
  const userRole = useSelector((state) => state.auth.user?.role);
  const channelRef = useRef(null);

  useEffect(() => {
    if (!echo || userRole !== roles.administrator) return undefined;
    if (channelRef.current) return undefined;
    if (!echo.private || typeof echo.private !== 'function') return undefined;

    let channel;
    try {
      channel = echo.private('admins');
      channelRef.current = channel;
    } catch (_) {
      return undefined;
    }

    channel.listen('.plan.request.created', (data) => {
      const planName = data?.plan_name ?? '';
      const requesterName = data?.requester?.name ?? '';
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: `plan-request-created-${Date.now()}`,
        variant: 'info',
        message: t('planRequest.created', { ns: 'notifications', planName, requesterName }),
        options: { persist: false, autoHideDuration: 8000 }
      })));
    });

    return () => {
      if (channelRef.current && echo && typeof echo.leave === 'function') {
        try {
          channelRef.current.stopListening('.plan.request.created');
          echo.leave('admins');
          channelRef.current = null;
        } catch (_) {
          // silent cleanup
        }
      }
    };
  }, [echo, userRole, dispatch, t]);
}

export default usePlanRequestAdminListener;
